import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bookKey,
  getBook,
  getChapter,
  getManifest,
  getPreparation,
  getRender,
  getRenderEstimate,
  latestRender,
  listVoices,
  startPreparation,
  startRender,
  type PartFailure,
  type Render,
  type RenderPart,
  type RenderMode,
} from "../api";
import Disclosure from "../components/Disclosure";
import MutationError from "../components/MutationError";
import PreparationPanel, { canPrepareFirst } from "../components/PreparationPanel";
import ScriptPanel from "./ScriptPanel";
import StopNarration from "../components/StopNarration";
import { useT } from "../i18n";
import NarrationSettings, { narrationSummary } from "./NarrationSettings";
import FailedPassage from "./FailedPassage";
import { partCounts } from "./partcounts";

/**
 * How long "prepare, then narrate" waits. Ten minutes at 1.5 s a poll, which
 * is well past the longest chapter measured and short enough that a stuck
 * piece gives the button back the same day.
 */
const MAX_PREPARE_POLLS = 400;

/**
 * NarrationPanel is everything about turning this chapter into audio: what it
 * would cost, what it would reuse, how the book is narrated, whether to prepare
 * the text first, whether to pay half and wait, and how the render is going.
 *
 * It used to be a third of ChapterPage — a 750-line component in which the page
 * and the narration held one pile of state between them, so a change to the
 * economy checkbox sat forty lines from a change to the editor's selection. The
 * page is now about which mode the author is in and what the chapter looks
 * like; this is about spending money on it.
 *
 * It fetches what it needs rather than being handed it. Every query here shares
 * a key with the page's, so TanStack serves both from one request — the page
 * still needs the manifest for the player and the estimate for "the audio is of
 * an earlier text", and threading them down as props would only mean two places
 * that have to agree about the key.
 */
export default function NarrationPanel({
  chapterID,
  bookID,
}: {
  chapterID: string;
  /** Absent until the chapter has loaded; the settings row waits for it. */
  bookID?: string;
}) {
  const t = useT();
  const client = useQueryClient();

  const chapter = useQuery({
    queryKey: ["chapter", chapterID, "draft"],
    queryFn: () => getChapter(chapterID, { draft: true }),
  });
  const book = useQuery({
    queryKey: bookKey(bookID ?? "", { draft: true }),
    queryFn: () => getBook(bookID!, { draft: true }),
    enabled: !!bookID,
  });
  const manifest = useQuery({
    queryKey: ["manifest", chapterID, "draft"],
    queryFn: () => getManifest(chapterID, { draft: true }),
    retry: false,
    gcTime: 0,
    staleTime: 0,
  });
  const estimate = useQuery({
    queryKey: ["estimate", chapterID],
    queryFn: () => getRenderEstimate(chapterID),
    retry: false,
  });
  const counts = estimate.data && partCounts(estimate.data);
  // Record again's price (ADR-0162), asked only of a chapter that has audio.
  // Under the same key prefix, so every refresh of the estimate refreshes it.
  const freshEstimate = useQuery({
    queryKey: ["estimate", chapterID, "fresh"],
    queryFn: () => getRenderEstimate(chapterID, true),
    enabled: !!manifest.data,
    retry: false,
  });

  /**
   * Which render this panel is watching.
   *
   * Seeded from the server rather than only from the tap that started it: the
   * id used to live in component state alone, so refreshing the page — or
   * opening the chapter on another device — showed nothing at all while the job
   * carried on running. A render is a fact about the chapter, not about the tab.
   */
  const [startedID, setStartedID] = useState<string | null>(null);
  const latest = useQuery({
    queryKey: ["latest-render", chapterID],
    queryFn: () => latestRender(chapterID),
    // 404 means "never rendered", which is a state, not a failure to retry.
    retry: false,
    staleTime: 0,
  });

  /**
   * A finished render is deliberately not adopted. Its story is told by the
   * audio that appeared; resurrecting "Finished · $0.01" on every page load for
   * a render from last week would be noise. Anything else — running, failed,
   * partial — is something the reader needs to see on arrival.
   */
  const watching =
    startedID ?? (latest.data && latest.data.status !== "complete" ? latest.data.id : null);

  const render = useQuery({
    queryKey: ["render", watching],
    queryFn: () => getRender(watching!),
    enabled: !!watching,
    // Seeded so the first paint after a refresh already shows progress instead
    // of a blank panel for one poll interval.
    initialData: latest.data && latest.data.id === watching ? latest.data : undefined,
    // The plain poll is the always-supported fallback; SSE is an optimisation
    // layered on top of it, not a replacement (docs/05-api-and-clients.md).
    refetchInterval: (query) => {
      const data = query.state.data as Render | undefined;
      if (!data) return 2000;
      return ["complete", "partial", "failed", "cancelled"].includes(data.status) ? false : 2000;
    },
  });

  /**
   * Economy is half price and arrives when the provider gets to it — minutes
   * for a short chapter, over an hour for a long one, against a 24-hour SLA.
   * It defaults off: a first narration that takes 72 minutes with no warning
   * reads as broken, and the saving is the user's call to make, not ours.
   */
  const [mode, setMode] = useState<RenderMode>("fast");
  const [economyInfo, setEconomyInfo] = useState(false);
  // A model with no batch queue (Soniox, ADR-0031) is narrated fast only, so
  // economy is not offered — and a choice made on another model is not sent.
  const catalogue = useQuery({
    queryKey: ["voices"],
    queryFn: listVoices,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const bookModel = book.data?.narration_model || catalogue.data?.default_model || "";
  const bookModelInfo = catalogue.data?.models?.find((m) => m.name === bookModel);
  const economyOffered = bookModelInfo?.batch ?? true;
  const renderMode: RenderMode = economyOffered ? mode : "fast";

  /**
   * Preparing first (ADR-0033) is offered where narrating is decided, as a
   * checkbox beside Narrate, rather than as a tool to find. Checked, the tap
   * prepares the pieces still unprepared, waits for them, then narrates — the
   * render is created only once the marks exist, because the marks are part
   * of its key. Leaving the page mid-way leaves the preparation running; the
   * chapter is then prepared, and Narrate is one tap.
   */
  const preparation = useQuery({
    queryKey: ["preparation", chapterID],
    queryFn: () => getPreparation(chapterID),
    retry: false,
  });
  // Both halves of the question: preparing is offered on this book at all
  // (ADR-0064), and there is text it has not marked yet. Asking only the
  // second offered ezafe on every English chapter, because on an English book
  // no piece is ever prepared.
  const unprepared = canPrepareFirst(preparation.data);
  const [prepareFirst, setPrepareFirst] = useState(false);
  const [preparingFor, setPreparingFor] = useState<{
    done: number;
    total: number;
  } | null>(null);

  const begin = useMutation({
    // No voice argument: the server resolves the book's, which is the one the
    // picker just saved. Sending it from here as well would be a second source
    // for the same answer.
    // `fresh` is Record again (ADR-0162): the same render, reusing nothing.
    mutationFn: async (fresh: boolean) => {
      if (prepareFirst && unprepared) {
        let status = await startPreparation(chapterID);
        setPreparingFor({
          done: status.complete + status.failed,
          total: status.total,
        });
        // Bounded. `while (pending > 0)` was unbounded, so one piece whose job
        // never reached a terminal state — a worker killed mid-flight leaves
        // exactly that — polled every 1.5 s for as long as the tab stayed open,
        // with a button that never came back.
        for (let i = 0; status.pending > 0 && i < MAX_PREPARE_POLLS; i++) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
          status = await getPreparation(chapterID);
          setPreparingFor({
            done: status.complete + status.failed,
            total: status.total,
          });
        }
        client.setQueryData(["preparation", chapterID], status);
        void client.invalidateQueries({ queryKey: ["estimate", chapterID] });
        setPreparingFor(null);
        // Narrating now would key the render on marks that do not exist yet,
        // and the pieces still being prepared would be silently left out of it.
        if (status.pending > 0) throw new Error(t.narration.prepareStillRunning);
      }
      return startRender(chapterID, renderMode, undefined, fresh);
    },
    // The answer goes into the cache, not only its id. A retry re-arms the
    // render already on screen, so the id is the same one and the cached copy
    // still says "partial" — which is settled, so nothing polled it and the
    // panel sat still until a reload. The answer says "pending", and polling
    // resumes from that.
    onSuccess: (r) => {
      client.setQueryData(["render", r.id], r);
      void client.invalidateQueries({ queryKey: ["latest-render", chapterID] });
      setStartedID(r.id);
    },
    onError: () => setPreparingFor(null),
  });

  // Parts that stopped without audio. Batch requests fail independently — one
  // of twelve, in practice — so the rest of the chapter is listenable and only
  // these need doing again.
  const failedParts = (render.data?.parts ?? []).filter((p) => p.status === "failed");
  const failureGroups = byFailure(failedParts);
  const retryableParts = failedParts.filter((p) => p.retryable ?? true).length;

  // "Settled" means the render stopped moving, whatever the outcome. Treating
  // only complete and partial as settled left a failed render showing
  // "Narrating 0 of 1…" for ever, with the button disabled and no reason on
  // screen — which is how a clear failure reads as a hang.
  const status = render.data?.status;
  const settled = !!status && ["complete", "partial", "failed", "cancelled"].includes(status);
  const succeeded = !!status && ["complete", "partial"].includes(status);

  // Audio appearing changes what the rest of the page shows: the player, the
  // chapter's own `has_active_render`, and what narrating again would cost.
  useEffect(() => {
    if (succeeded) {
      void client.invalidateQueries({ queryKey: ["manifest", chapterID] });
      void client.invalidateQueries({ queryKey: ["chapter", chapterID] });
      void client.invalidateQueries({ queryKey: ["estimate", chapterID] });
    }
  }, [succeeded, chapterID, client]);

  return (
    <section
      data-testid="render-controls"
      className="mb-8 rounded-card border border-line bg-surface p-4"
    >
      {/* Staleness is a hash comparison the server already makes, so it
          costs nothing to show — and without it a saved correction is
          invisible until somebody happens to re-narrate.

          Gated on there being a render to be stale *against*. `needs_render`
          answers "does this chapter need narrating", which is true of a
          chapter that has never been narrated at all — but this sentence
          claims the text changed *since it was narrated*, which for a
          chapter created a moment ago is simply false, and it rendered
          directly above "This chapter has no audio yet." */}
      {/* `needs_render` is the server's answer to "would narrating change
          what anybody hears", not "does the render key differ" — a chapter
          edited to say what its correction already said differs and would
          come back identical, and there is nothing to tell the author about
          that. What is said here is only ever about work that would
          actually be done. */}
      {estimate.data?.needs_render && chapter.data?.has_active_render && (
        <p
          data-testid="needs-render"
          className="mb-3 rounded-control bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
        >
          {/* Only re-levelling to do (ADR-0178): nothing the author changed,
              and nothing to pay for. */}
          {counts?.relevelOnly
            ? t.narration.levelsChanged
            : estimate.data.performed
              ? t.narration.performedChanged
              : t.narration.textChanged}
          {counts &&
            !counts.relevelOnly &&
            counts.carried > 0 &&
            t.narration.onlyRerecorded(counts.recorded, estimate.data.chunks)}
        </p>
      )}

      {/* A chapter the server will not narrate says why before the button is
          pressed — a book with no language on a model that must be told one
          (ADR-0057), a part too long for the model. */}
      <MutationError
        testId="estimate-error"
        error={estimate.error}
        fallback={t.common.somethingWrong}
      />
      {estimate.data && (
        <p className="text-sm text-ink-soft" data-testid="render-estimate">
          {t.narration.parts(estimate.data.chunks)} ·{" "}
          {/* The price of narrating now. With nothing to reuse that is the
              whole chapter, as a range; with parts to carry over it is what
              the remaining ones cost, which is a number, not a guess. */}
          {counts && counts.carried > 0 ? (
            <span data-testid="estimate-remaining" dir="ltr">
              {estimate.data.estimate.remaining_display}
            </span>
          ) : (
            <span data-testid="estimate-range" dir="ltr">
              {estimate.data.estimate.display_range}
            </span>
          )}
          {estimate.data.reusable > 0 && (
            <span data-testid="reusable-count">
              {" "}
              · {t.narration.alreadyNarrated(estimate.data.reusable)}
            </span>
          )}
          {counts && counts.refinish > 0 && (
            <span data-testid="refinish-count"> · {t.narration.relevelled(counts.refinish)}</span>
          )}
        </p>
      )}

      {/* A performed chapter says whose voices it uses (ADR-0157); one the
          server refuses for want of a script says so in the error above. */}
      {estimate.data?.performed && (
        <p className="mt-1 text-sm text-ink-soft" data-testid="performed-voices">
          {estimate.data.voices && estimate.data.voices.length > 0
            ? t.narration.performedWith(
                estimate.data.voices
                  .map((v) => t.narration.performedLines(v.name, v.lines))
                  .join(", "),
              )
            : t.narration.performedNoVoices}
        </p>
      )}

      {book.data && bookID && (
        <Disclosure
          testId="narration-settings-row"
          toggleTestId="narration-settings-toggle"
          title={t.narration.settings}
          summary={narrationSummary(book.data, catalogue.data, t)}
          className="mt-3"
        >
          <NarrationSettings bookID={bookID} />
        </Disclosure>
      )}

      {/* Changing the voice — or the speech model moving under the
          library, which is ADR-0017 — re-records the whole chapter, and
          nothing else on this panel says so: `reusable` counts parts whose
          *text* is unchanged, and every one of them is still re-recorded.
          The comparison is against what listeners are actually hearing,
          because that is the only thing a re-record would replace. */}
      {estimate.data &&
        latest.data?.status === "complete" &&
        (latest.data.voice !== estimate.data.voice ||
          latest.data.model !== estimate.data.model) && (
          <p
            data-testid="voice-changed"
            className="mt-3 rounded-control bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
          >
            {latest.data.voice !== estimate.data.voice
              ? t.narration.voiceChanged(
                  latest.data.voice,
                  estimate.data.voice,
                  estimate.data.chunks,
                )
              : t.narration.modelChanged(estimate.data.chunks)}
          </p>
        )}

      <PreparationPanel chapterID={chapterID} />
      {bookID && <ScriptPanel bookID={bookID} chapterID={chapterID} />}

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        <button
          type="button"
          data-testid="narrate"
          onClick={() => begin.mutate(false)}
          disabled={begin.isPending || (!!render.data && !settled)}
          className="rounded-control bg-sage px-4 py-2 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
        >
          {manifest.data ? t.narration.renarrate : t.narration.narrate}
        </button>
        {/* A chapter recorded before a finish change keeps its old audio,
            because every part is reused (0157 §10). Record again buys the
            whole chapter anew, and says what that costs on the button. */}
        {manifest.data && freshEstimate.data && (
          <button
            type="button"
            data-testid="record-again"
            title={t.narration.recordAgainTitle(freshEstimate.data.chunks)}
            onClick={() => begin.mutate(true)}
            disabled={begin.isPending || (!!render.data && !settled)}
            className="rounded-control border border-line px-4 py-2 text-sm text-ink transition-colors hover:border-line-strong disabled:opacity-40"
          >
            {t.narration.recordAgain} ·{" "}
            <span data-testid="record-again-price" dir="ltr">
              {freshEstimate.data.estimate.display_range}
            </span>
          </button>
        )}
        {unprepared && (
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-soft select-none">
            <input
              type="checkbox"
              data-testid="prepare-first"
              checked={prepareFirst}
              disabled={begin.isPending}
              onChange={(e) => setPrepareFirst(e.target.checked)}
              className="size-3.5 accent-[var(--c-sage)]"
            />
            {t.narration.prepareFirst}
          </label>
        )}
        {/* The trade-off is real and worth reading once, not on every visit —
          two lines of explanation beside a checkbox becomes furniture the
          eye skips, which is the worst outcome for a cost decision. */}
        {economyOffered && (
          <div className="flex items-center gap-2 text-sm text-ink-soft">
            <label className="flex cursor-pointer items-center gap-2 select-none">
              <input
                type="checkbox"
                data-testid="economy-mode"
                checked={mode === "economy"}
                onChange={(e) => setMode(e.target.checked ? "economy" : "fast")}
                className="size-3.5 accent-[var(--c-sage)]"
              />
              {t.narration.economy}
            </label>
            <button
              type="button"
              data-testid="economy-info"
              aria-expanded={economyInfo}
              aria-controls="economy-explainer"
              onClick={() => setEconomyInfo((open) => !open)}
              className="grid size-5 place-items-center rounded-full border border-line text-[0.7rem] font-medium text-muted transition-colors hover:border-line-strong hover:text-ink"
            >
              <span aria-hidden="true">i</span>
              <span className="sr-only">{t.narration.whatIsEconomy}</span>
            </button>
          </div>
        )}
      </div>

      {economyOffered && economyInfo && (
        <p
          id="economy-explainer"
          data-testid="economy-explainer"
          className="mt-2 rounded-control bg-sunk px-3 py-2 text-xs text-ink-soft"
        >
          {t.narration.economyExplainer}
        </p>
      )}

      {begin.isError && (
        <p
          role="alert"
          data-testid="render-error"
          className="mt-3 text-sm text-red-600 dark:text-red-400"
        >
          {begin.error instanceof Error ? begin.error.message : t.narration.startFailed}
        </p>
      )}

      {preparingFor && (
        <p className="mt-3 text-sm text-muted" data-testid="preparing-first">
          {t.narration.preparingFirst(preparingFor.done, preparingFor.total)}
        </p>
      )}

      {render.data && !settled && (
        <p className="mt-3 text-sm text-muted" data-testid="render-progress">
          {t.narration.narrating(render.data.chunk_done, render.data.chunk_total)}
          {render.data.mode === "economy" && (
            <span data-testid="economy-waiting">{t.narration.economyWaiting}</span>
          )}
          <StopNarration renderID={render.data.id} />
        </p>
      )}
      {render.data && status === "cancelled" && (
        <p className="mt-3 text-sm text-muted" data-testid="render-cancelled">
          {t.narration.cancelled}
        </p>
      )}
      {render.data && succeeded && (
        <p className="mt-3 text-sm text-muted" data-testid="render-done">
          {render.data.status === "partial" ? t.narration.finishedPartial : t.narration.finished} ·{" "}
          {render.data.cost_usd}
        </p>
      )}

      {/* Which passages have no audio, as their words and not their numbers
          (ADR-0120), one group per cause: an exhausted quota fails most of a
          chapter at once, and saying why sixteen times hid which passages
          they were. Pressing Narrate again re-arms this render: the parts that
          succeeded keep their audio, so a retry costs only these. */}
      {failedParts.length > 0 && (
        <div data-testid="failed-parts" className="mt-3 text-sm">
          <p className="text-amber-700 dark:text-amber-400">
            {t.narration.partsFailed(failedParts.length)}
          </p>
          <ul className="mt-3 space-y-4">
            {failureGroups.map((g) => (
              <li key={g.failure} data-testid="failure-group" data-failure={g.failure}>
                <ul className="space-y-3">
                  {g.parts.map((p) => (
                    <FailedPassage
                      key={p.index}
                      chapterID={chapterID}
                      partID={p.id ?? ""}
                      text={p.text ?? ""}
                      failure={g.failure}
                      unsaidStart={p.unsaid_start}
                      unsaidEnd={p.unsaid_end}
                      retryable={p.retryable ?? true}
                      message={p.error}
                    />
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          {/* Only when asking again can give a different answer: the server
              says which causes those are (ADR-0120). */}
          {retryableParts > 0 && (
            <button
              type="button"
              data-testid="retry-failed"
              onClick={() => begin.mutate(false)}
              disabled={begin.isPending}
              className="mt-3 rounded-control border border-line px-3 py-1.5 text-sm disabled:opacity-40"
            >
              {t.narration.problemTryAgain(retryableParts)}
            </button>
          )}
        </div>
      )}

      {/* A render that stopped without producing audio has to say why and
          offer another go. Pressing Narrate again re-arms the same render
          and only regenerates the parts that failed. */}
      {render.data && status === "failed" && (
        <p
          role="alert"
          data-testid="render-failed"
          className="mt-3 text-sm text-red-600 dark:text-red-400"
        >
          {t.narration.failed(render.data.error || undefined)}
        </p>
      )}
    </section>
  );
}

/** Failed parts by cause, in the order each cause first appears. */
function byFailure(parts: RenderPart[]): { failure: PartFailure; parts: RenderPart[] }[] {
  const groups = new Map<PartFailure, RenderPart[]>();
  for (const p of parts) {
    const failure = p.failure ?? "unknown";
    groups.set(failure, [...(groups.get(failure) ?? []), p]);
  }
  return [...groups].map(([failure, members]) => ({ failure, parts: members }));
}
